// Knock Reports — GET /operations/field-sales-attribution/, printed as the backend
// counts it and labelled with WHAT each number counts. The endpoint mixes two knock
// systems: total_knocks counts knocks logged in the app (DoorKnockEvent), while
// knock_to_lead_count counts leads linked to a FieldRoutes door knock
// (operations.DoorKnock). Its close_rate divides customers-from-any-lead by the
// second, so it is not a close rate and is not shown; the rest are labelled for
// what they are. Unknown is "—", never 0.
import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { getUserRole } from '../../helpers/tenantHelpers';
import { DataTable } from '../../components/ui/table';
import RevivalHeader, { StatStrip } from '../../components/Revival/RevivalHeader';
import { count, money, words } from './salesFormat';
import '../../components/Revival/revival.css';
import './sales-ds.css';

const MANAGER = ['admin', 'owner', 'manager', 'ceo'];

export default function KnockReports() {
  const isManager = MANAGER.includes(String(getUserRole() || '').toLowerCase());
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let alive = true;
    api.get('/operations/field-sales-attribution/')
      .then((res) => { if (alive) { setData(res.data || {}); setState('ready'); } })
      .catch((err) => { if (alive) setState((err?.response?.status ?? err?.status) === 403 ? 'forbidden' : 'error'); });
    return () => { alive = false; };
  }, []);

  const d = data || {};
  const bySource = Object.entries(d.revenue_by_source || {}).map(([source, revenue]) => ({ source, revenue }));
  const byRep = (d.revenue_by_rep || []).map((r) => ({ ...r, id: r.rep_id }));

  return (
    <div className="rv-screen">
      <RevivalHeader title="Knock reports" totals={state === 'ready' ? [<>Revenue <b>{money(d.total_revenue)}</b></>] : []} />
      {state === 'loading' ? <div className="rv-loading">Loading reports…</div> : null}
      {state === 'forbidden' ? <p className="sl-notice">Knock reports are for owners and managers.</p> : null}
      {state === 'error' ? <p className="sl-notice is-bad">Knock reports didn’t load. Reload the page to try again.</p> : null}
      {state === 'ready' ? (
        <>
          <StatStrip stats={[
            { label: 'Knocks logged in the app', value: count(d.total_knocks) },
            { label: 'Leads from FieldRoutes knocks', value: count(d.knock_to_lead_count) },
            { label: 'Customers created from a lead', value: count(d.lead_to_customer_count) },
            { label: 'Customers with FieldRoutes jobs', value: count(d.customer_to_job_count) },
          ]} />
          <StatStrip stats={[
            { label: 'Revenue (all FieldRoutes)', value: money(d.total_revenue) },
            { label: 'Revenue ÷ active customers', value: money(d.avg_ticket) },
            { label: 'Active customers', value: count(d.total_customers) },
            { label: 'Customers with no source', value: count(d.attribution_gaps) },
          ]} />
          <p className="sl-notice is-warn">
            Knocks and knock leads come from two different systems (the app, and FieldRoutes), so no close rate is shown.
          </p>
          <section className="sl-section">
            <div className="sl-section-head"><h2>Revenue by source</h2></div>
            <DataTable
              rowKey="source"
              columns={[
                { key: 'source', label: 'Source', render: (r) => words(r.source) },
                { key: 'revenue', label: 'Revenue', num: true, render: (r) => money(r.revenue) },
              ]}
              rows={bySource}
              initialSort={{ key: 'revenue', dir: 'desc' }}
              empty="No revenue is linked to a customer source yet."
            />
          </section>
          {isManager ? (
            <section className="sl-section">
              <div className="sl-section-head"><h2>Revenue by rep</h2></div>
              <DataTable
                columns={[
                  { key: 'rep_name', label: 'Rep', render: (r) => r.rep_name || '—' },
                  { key: 'revenue', label: 'Revenue', num: true, render: (r) => money(r.revenue) },
                ]}
                rows={byRep}
                initialSort={{ key: 'revenue', dir: 'desc' }}
                empty="No customer has an acquisition rep yet."
              />
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
